/**
 * Pure decision logic for `DailyChallengeScene.startAttempt()` - no Phaser
 * dependency, so it's directly unit-testable (see
 * `src/daily/__tests__/attemptReservation.test.ts`) without a Scene.
 *
 * `DailyChallengeClient.reserveAttempt()` is the one place an attempt is
 * actually spent server-side - everything here only decides what the
 * scene should DO with the result it already got back, never whether to
 * call it again.
 */

import type { DailyAttemptReserveResult } from "./DailyChallengeClient";

/**
 * Whether `startAttempt()` may call `reserveAttempt()` right now - the
 * single gate that guarantees at most one in-flight `daily-attempt`
 * request. Requires all four: not already reserving (re-entrancy/double
 * click), a loaded challenge spec, the *authoritative* status having
 * finished loading (`hasLoadedStatus` - never true while the screen is
 * still on "loading"/"loading-status", and never true after a failed
 * status load either), and a genuinely positive attempts-remaining count.
 * That last check is defense-in-depth alongside the server's own 409 -
 * once the loaded status already says zero, this client deliberately
 * never issues the request at all.
 */
export function canStartAttempt(isReserving: boolean, hasSpec: boolean, hasLoadedStatus: boolean, attemptsRemaining: number): boolean {
  return !isReserving && hasSpec && hasLoadedStatus && attemptsRemaining > 0;
}

export type AttemptOutcome =
  | { type: "start-game"; attemptNumber: number; attemptsRemaining: number }
  | { type: "exhausted" }
  | { type: "failed"; loadState: "offline" | "error"; message: string };

/**
 * Maps a `reserveAttempt()` result onto exactly one outcome the scene acts
 * on - never more than one, and never none. A response that claims success
 * but is missing the fields a real reservation always carries
 * (`attemptNumber`/`attemptsRemaining`) is treated as `failed`, not
 * `start-game` - the caller must never transition into a run without a
 * genuine reservation.
 */
export function resolveAttemptOutcome(result: DailyAttemptReserveResult): AttemptOutcome {
  if (result.state === "exhausted") {
    return { type: "exhausted" };
  }
  if (result.state === "ok" && result.attemptNumber !== undefined && result.attemptsRemaining !== undefined) {
    return { type: "start-game", attemptNumber: result.attemptNumber, attemptsRemaining: result.attemptsRemaining };
  }
  return {
    type: "failed",
    loadState: result.state === "offline" ? "offline" : "error",
    message: result.message ?? "Could not start your attempt.",
  };
}
